import { z } from 'zod';
import { trackedItemStatusSchema } from './tracked-item-status';

/**
 * A stored run is only as readable as its loosest writer: one wrote `metricQualifier: ""` where this
 * schema wants at least one character, and every read of that run failed for fifteen hours — gone
 * from the UI, gone from the standing window. An empty optional string reads as absent.
 */
const emptyAsAbsent = <T extends z.ZodType<string>>(schema: T) =>
  z.preprocess((value) => (value === '' ? undefined : value), schema.optional());

/**
 * `dataReadiness` exists so a finding about what the data cannot yet measure stops being filed as a
 * risk. A feed where every card is a risk trains a reader to discount all of them, which costs the
 * agent the credibility it needs on the one card that matters.
 */
export const findingGroups = ['risk', 'opportunity', 'ontrack', 'dataReadiness'] as const;

export const continuityStatuses = ['new', 'kept', 'updated'] as const;
export const retiredReasons = ['resolved', 'not_observed', 'superseded'] as const;

/**
 * How this finding relates to the previous runs' findings. `kept` and `updated` continue a standing
 * finding (`continues` is its id); the chain's origin travels along so a card can say "since <date>".
 * Absent on runs written before persistence existed.
 */
export const findingContinuitySchema = z.object({
  status: z.enum(continuityStatuses),
  continues: z.string().min(1).optional(),
  firstSeenRunId: z.string().min(1).optional(),
  firstSeenAt: z.string().optional(),
  /** The standing finding's metric when `updated` moved it. */
  previousMetric: z.string().optional(),
  /** One line on what changed, for `updated`. */
  note: z.string().optional(),
});

/** The writer's own word on a standing finding it is not carrying forward. */
export const retiredDeclarationSchema = z.object({
  id: z.string().min(1),
  reason: z.enum(retiredReasons),
  note: z.string().min(1),
});

/**
 * `Unverified` is not a weaker `Low`. It means an alternative explanation was tested, could not be
 * eliminated, and would invalidate the finding outright if true — so the claim stands only pending a
 * check a human has to make. `Low` remains the level for a claim the evidence does not support at
 * all, and is kept for stored runs written before `Unverified` existed.
 *
 * Never self-reported by the agent as final: `critics/derive-confidence.ts` recomputes it from the
 * alternatives, the grounding verdict and the reconciliation verdict. The agent's own value is only
 * ever a ceiling.
 */
export const confidenceLevels = ['High', 'Medium', 'Unverified', 'Low'] as const;

/**
 * How much each level is worth, ascending. Lives here rather than in either app because both need
 * it and neither owns it: `apps/sir` caps a finding's confidence by comparing ranks, and the
 * frontend falls back to this order when sorting findings with no `decisionRank`. Two hand-written
 * copies would let one side reorder the enum without the other noticing.
 */
export const CONFIDENCE_RANK: Record<(typeof confidenceLevels)[number], number> = {
  Low: 0,
  Unverified: 1,
  Medium: 2,
  High: 3,
};

/**
 * Why a finding carries the confidence it does, as a code the UI renders in the reader's language.
 * Stated on the chip so it carries information rather than decoration — "High confidence" repeated on
 * every card of a run, including one whose own evidence says it should be verified before escalating,
 * is the failure this replaces.
 */
export const confidenceBases = [
  'noUnresolvedAlternative',
  'magnitudeUnresolved',
  'couldBeInvalidated',
  'claimsUnsupported',
  'notCheckable',
  'contradictsSourceOfTruth',
  'referencesUnresolved',
  'countContradicted',
] as const;

/**
 * The slice of the portfolio a finding describes, in the vocabulary of the signals RFC
 * (NEX-15587): the level it was measured at. Optional on the schema rather than required
 * because `getLatestRun` re-validates stored runs — runs written before this field existed
 * are the evidence log for the POC and must keep loading. The prompt requires it.
 */
export const findingScopeLevels = ['loop', 'objective', 'initiative', 'team'] as const;

export const findingScopeSchema = z.object({
  level: z.enum(findingScopeLevels),
  ref: z.string().optional(),
});

/**
 * SIR's domain model 6.19: a recommendation is generated only for an `action`-kind insight; a
 * `report`-kind one is terminal awareness. Worth adopting because forcing a directive onto a
 * finding that only warrants awareness is what produced the weakest actions in the hand-authored
 * baseline. Optional for the same stored-run reason as `scope`.
 */
export const findingKinds = ['action', 'report'] as const;

/**
 * The actionability judge's mark on a recommendation (critics/review-actionability.ts). `approved` is
 * true only when the recommendation clears all four axes: it is a concrete lever (not a vague intention
 * or a go-find-out the analysis should have resolved), its success check re-measures the OBJECTIVE it
 * moves rather than the task, it is addressed to an owner with the authority to act, and it targets the
 * root cause. This is an ANNOTATION, never a gate — no recommendation is dropped, hidden or reordered
 * on account of it; the mark exists so an internal reviewer can calibrate the judge against their own
 * read before it ever influences what a user sees. `summary` is the judge's one-line reason.
 */
export const recommendationActionabilitySchema = z.object({
  approved: z.boolean(),
  concreteLever: z.boolean(),
  movesTheObjective: z.boolean(),
  ownerCanExecute: z.boolean(),
  addressesRootCause: z.boolean(),
  summary: z.string().min(1).optional(),
});
export type RecommendationActionability = z.infer<typeof recommendationActionabilitySchema>;

/**
 * A person's disposition on a finding or recommendation, joined onto the run at read time from the
 * tracked-items table — never stored in the run itself. Absent when nobody has acted on it.
 */
export const trackingRefSchema = z.object({
  trackedItemId: z.string().min(1),
  status: trackedItemStatusSchema,
  assigneeUserId: z.string().nullable(),
});
export type TrackingRef = z.infer<typeof trackingRefSchema>;

export const recommendationSchema = z.object({
  /**
   * Derived by `withStableRecommendationIds`, never model-authored — a correlation key for
   * per-recommendation tracking (pin/assign/dismiss). Absent on runs stored before it existed.
   */
  id: z.string().min(1).optional(),
  tracking: trackingRefSchema.optional(),
  title: z.string().min(1),
  businessTitle: z.string().min(1),
  detail: z.string().min(1),
  steps: z.array(z.string().min(1)).optional(),
  target: z.string().min(1),
  impact: z.string().min(1),
  primary: z.boolean().optional(),
  /**
   * When the action is due — a date or a planning horizon ("25 Aug 2026", "Next planning cycle").
   * Its own field rather than a clause inside `successCriterion`, because an action with an owner
   * and a check but no date is the one that never gets done, and a reader scanning for the timeline
   * should not have to read a sentence to find it. Optional so stored runs keep loading.
   */
  deadline: z.string().min(1).optional(),
  /**
   * The observable that would confirm or refute the recommendation — SIR's domain model 6.10. The
   * horizon it used to carry now lives in `deadline`. Optional on the schema so stored runs written
   * before it existed still load; the prompt asks for it on every recommendation.
   */
  successCriterion: z.string().min(1).optional(),
  /**
   * True when the honest answer is that nothing needs doing. A feed with no slot for "nothing to do"
   * manufactures busywork on every healthy finding, which is how an on-track card ends up reading
   * like a risk.
   */
  noActionNeeded: z.boolean().optional(),
  /**
   * The actionability judge's annotate-only mark (never a gate). Absent on stored runs written before
   * it existed, and absent when the judge could not be reached — so read it as "unmarked", not "failed".
   */
  actionability: recommendationActionabilitySchema.optional(),
});

/**
 * One alternative explanation the agent tested, with its verdict — the structure that lets confidence
 * be derived rather than asserted.
 *
 * `impact` is what makes the verdict actionable: an unresolved alternative that would make the
 * finding wrong is a different thing from one that would only change its size, and they cap
 * confidence differently (see `critics/derive-confidence.ts`).
 */
export const alternativeVerdicts = ['eliminated', 'notEliminated', 'untestable'] as const;
export const alternativeImpacts = ['invalidates', 'magnitude', 'none'] as const;

export const alternativeSchema = z.object({
  statement: z.string().min(1),
  verdict: z.enum(alternativeVerdicts),
  impact: z.enum(alternativeImpacts),
  note: z.string().min(1),
});

/**
 * One source that fed a finding, named for what it is rather than for what it is called in code, and
 * stated as a sentence a reader who has never seen the API can check. The tool syntax lives in
 * `evidence.observed`, one click away, for the different reader who wants to re-run it.
 */
export const evidenceSourceSchema = z.object({
  label: z.string().min(1),
  statement: z.string().min(1),
});

export const evidenceSchema = z.object({
  /**
   * The raw tool calls and ids. Still required — it is what the grounding critic checks against — but
   * no longer the reader-facing provenance: it sits behind a disclosure in the UI.
   */
  observed: z.string().min(1),
  /** Plain-language provenance, one row per source. Optional only so stored runs keep loading. */
  sources: z.array(evidenceSourceSchema).optional(),
  /** Alternatives with a verdict each. Optional only so stored runs keep loading. */
  alternatives: z.array(alternativeSchema).optional(),
  /**
   * Deprecated. A projection worth making belongs in the finding's own first line, not in a labelled
   * block below the fold where the card's actual conclusion goes unread. No longer requested by the
   * prompt; retained so stored runs still parse.
   */
  projected: z.string().min(1).optional(),
  /** Superseded by `alternatives`, which carries the same honesty in a form the UI can grade. */
  ruledOut: z.string().min(1).optional(),
  trackRecord: z.string().min(1).optional(),
});

/**
 * One supporting fact, not a paragraph. `lead` is the identifier or the claim itself, `text` the
 * figures on one line, `note` the commentary that gets demoted to grey — so the item ids a reader
 * needs in order to act can be scanned rather than dug out of prose.
 */
export const descriptionBlockSchema = z.object({
  lead: z.string().min(1),
  text: z.string().min(1),
  note: z.string().min(1).optional(),
});

export const personaOverrideSchema = z.object({
  businessStatement: z.string().min(1),
  recommendations: z.array(recommendationSchema).min(1),
});

/**
 * What kind of figure a grounded claim rests on, so a later deterministic replay knows how to compare
 * a re-read value against the captured one without an LLM: a percentage/progress figure compares by
 * tolerance, a health-style state by exact match (or a known "resolved" transition), a count against a
 * fixed limit, a yes/no presence check, or anything else expressed as a fraction.
 */
export const metricShapes = [
  'percentOfTotal',
  'healthLevel',
  'countAgainstThreshold',
  'existence',
  'ratio',
] as const;
export type MetricShape = (typeof metricShapes)[number];
export const metricShapeSchema = z.enum(metricShapes);

/** One tool call, named precisely enough to reissue it later without the model's help. */
export const groundingFactQuerySchema = z.object({
  tool: z.string().min(1),
  args: z.record(z.string(), z.unknown()).optional(),
  /** Where in that call's parsed JSON result `capturedValue` was found, e.g. `objectives[0].progressPercentage`. */
  resultPath: z.string().min(1),
});
export type GroundingFactQuery = z.infer<typeof groundingFactQuerySchema>;

/**
 * A structured, replayable alternative to `continuationHolds()`'s text-similarity guess: the exact
 * tool call and value a finding's primary claim rests on, derived entirely by code from a
 * `[[fact:N]]` marker the model cites — never trusted as model-authored prose. Absent whenever a
 * finding's claim could not be traced to exactly one tool-result value (most findings, in v1).
 */
export const findingGroundingSchema = z.object({
  version: z.literal(1),
  metricShape: metricShapeSchema,
  primaryFact: groundingFactQuerySchema,
  capturedValue: z.union([z.number(), z.string(), z.boolean()]),
  capturedAt: z.string(),
  capturedInRunId: z.string().min(1),
});
export type FindingGrounding = z.infer<typeof findingGroundingSchema>;

export const findingSchema = z.object({
  id: z.string().min(1),
  group: z.enum(findingGroups),
  confidence: z.enum(confidenceLevels),
  /** Derived alongside `confidence`; absent on stored runs written before it existed. */
  confidenceBasis: z.enum(confidenceBases).optional(),
  kind: z.enum(findingKinds).optional(),
  scope: findingScopeSchema.optional(),
  objectiveIds: z.array(z.string()),
  /**
   * Ids of the specific key results this finding's recommendations are meant to move — the
   * measurable outcomes one level below the objective. Optional and absent when the finding is about
   * the objective as a whole or no single KR; stored runs written before this existed omit it.
   */
  keyResultIds: z.array(z.string()).optional(),
  /**
   * Ids of the candidates this finding was written from, when the run authored from a fanned
   * candidate set. Lineage, and deliberately a HINT rather than a fact: an authoring pass merges two
   * candidates into one finding and renames a third, so a candidate absent from every finding's list
   * has not necessarily gone unreported. Never infer "this candidate was dropped" from its absence
   * here — that inference was measured wrong 3 times in 5 on a real run. Absent on runs authored the
   * single-agent way.
   */
  candidateIds: z.array(z.string()).optional(),
  continuity: findingContinuitySchema.optional(),
  /**
   * When the plain-voice pass last rewrote this finding's reader-facing prose. Set only by that pass,
   * never by the writer, and copied forward with a `kept` finding's wording. It is what lets the pass
   * skip a kept finding: the stored words were already rewritten, and translating them again every
   * run would drift the cards persistence exists to hold still. A kept finding WITHOUT it — stored
   * before the pass existed, or whose last rewrite failed — is rewritten on the next run it appears
   * in, so every loop backfills itself once with no flag to flip. Optional so stored runs keep loading.
   */
  plainVoicedAt: z.string().min(1).optional(),
  /**
   * Which version of the plain-voice prompt wrote the stamp above (`PLAIN_VOICE_VERSION` in
   * `apps/sir`). A kept finding whose stamp is from an older version is rewritten once more, so a
   * better voice reaches the cards persistence holds still. Absent on findings stamped before the
   * version existed, which reads as "older than any version" and rewrites them once.
   */
  plainVoiceVersion: z.number().int().positive().optional(),
  /**
   * The signal types (e.g. "sprint.dependency_broken") whose detections this finding rests on — the
   * precomputed conditions the agent cited rather than re-derived. Optional and absent when the
   * finding draws on no signal; stored runs written before this existed omit it.
   */
  signalRefs: z.array(z.string()).optional(),
  headline: z.string().min(1),
  businessStatement: z.string().min(1),
  /**
   * The qualification that belongs in the same sentence as the claim — "but the effort data
   * contradicts itself". Parked below the fold it gets skipped, and the headline then overstates.
   */
  caveat: z.string().min(1).optional(),
  /**
   * One plain sentence, shown directly under the objective's progress bar, saying how this finding
   * bears on THAT objective's progress or date — "Strategy is one of the pieces this objective
   * promised to deliver, and at 0% it is what will make the date miss." The bar's percentage is the
   * objective's; this line is why the card sits under it. Optional so stored runs keep loading; the
   * UI shows the finding's own figure line when it is absent.
   */
  objectiveTie: z.string().min(1).optional(),
  metric: z.string(),
  metricLabel: z.string(),
  /**
   * What the metric is not precise about — "or more", "/ 240h planned". Precision has to follow
   * certainty: four significant figures on a number the card itself calls uncertain is the tell that
   * the headline was written without reading the caveat.
   */
  metricQualifier: emptyAsAbsent(z.string().min(1)),
  /**
   * What a portfolio-wide figure is measured against, e.g. "1,691 open items". Required by the prompt
   * on any loop-scope aggregate: the same counts divided by all work including closed work read an
   * order of magnitude differently, and a percentage with no stated base is not checkable.
   */
  denominator: emptyAsAbsent(z.string().min(1)),
  /**
   * Rank by decision value — 1 is what the reader should act on first. Ordering by objective id
   * produces one card per objective, which is enumeration rather than selection.
   */
  decisionRank: z.number().int().positive().optional(),
  description: z.array(descriptionBlockSchema),
  evidence: evidenceSchema,
  /**
   * The deterministic evidence-replay contract for this finding's primary claim, when one could be
   * derived. Optional and absent on most findings — only set when exactly one tool-result value could
   * be traced as the claim's source; never model-authored, always code-derived.
   */
  grounding: findingGroundingSchema.optional(),
  tracking: trackingRefSchema.optional(),
  recommendations: z.array(recommendationSchema).min(1),
  personaOverrides: z.record(z.string(), personaOverrideSchema).optional(),
  withheldFrom: z.record(z.string(), z.string().min(1)).optional(),
});

export const agentResultSchema = z.object({
  findings: z.array(findingSchema),
  /** Present only when the writer was shown standing findings and chose to retire some. */
  retired: z.array(retiredDeclarationSchema).optional(),
});

export type FindingGroup = (typeof findingGroups)[number];
export type ContinuityStatus = (typeof continuityStatuses)[number];
export type RetiredReason = (typeof retiredReasons)[number];
export type FindingContinuity = z.infer<typeof findingContinuitySchema>;
export type RetiredDeclaration = z.infer<typeof retiredDeclarationSchema>;
export type ConfidenceLevel = (typeof confidenceLevels)[number];
export type ConfidenceBasis = (typeof confidenceBases)[number];
export type FindingScopeLevel = (typeof findingScopeLevels)[number];
export type FindingKind = (typeof findingKinds)[number];
export type AlternativeVerdict = (typeof alternativeVerdicts)[number];
export type AlternativeImpact = (typeof alternativeImpacts)[number];
export type Alternative = z.infer<typeof alternativeSchema>;
export type EvidenceSource = z.infer<typeof evidenceSourceSchema>;
export type Recommendation = z.infer<typeof recommendationSchema>;
export type Evidence = z.infer<typeof evidenceSchema>;
export type DescriptionBlock = z.infer<typeof descriptionBlockSchema>;
export type FindingScope = z.infer<typeof findingScopeSchema>;
export type Finding = z.infer<typeof findingSchema>;
export type PersonaOverride = z.infer<typeof personaOverrideSchema>;
export type AgentResult = z.infer<typeof agentResultSchema>;
